import { AttachmentApiService, BannerApiService, CategoryApiService, FileType, ProductApiService, TagApiService, UploadResult } from '@/core';
import { CommonStatusValues, BannerTypeValues, EnableValues } from '@/shared';
import { formControl, Validators } from '@al00x/forms';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  APIHelper,
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacDialogLayoutComponent,
  CacFormBuilderComponent,
  CacImageUploaderComponent,
  formBuilder,
} from '@controladad/ng-base';
import { BannerCreate, BannerEntity, BannerStatus, BannerType, BannerUpdate, ValueOfProduct } from '@repo/types';
import { map, of } from 'rxjs';

export interface BannerDialogData {
  banner?: BannerEntity;
}

export type BannerDialogResult = boolean;

@Component({
  selector: 'feature-banner-dialog',
  standalone: true,
  imports: [CacDialogLayoutComponent, CacFormBuilderComponent, CacImageUploaderComponent],
  templateUrl: './banner-dialog.component.html',
  styleUrl: './banner-dialog.component.scss',
})
export class BannerDialogComponent extends CacBaseDialogComponent<BannerDialogData, BannerDialogResult> {
  private readonly bannerApi = inject(BannerApiService);
  private readonly tagApi = inject(TagApiService);
  private readonly categoryApi = inject(CategoryApiService);
  private readonly productApi = inject(ProductApiService);
  image = formControl<string | UploadResult>(null, Validators.required);

  form = formBuilder({
    cols: 2,
    inputs: {
      title: {
        control: formControl<string>(undefined, Validators.required),
        label: 'Title',
      },
      priority: {
        control: formControl<number>(undefined, Validators.required),
        label: 'Priority',
        inputType: 'number',
      },
      type: {
        control: formControl<BannerType>(undefined, Validators.required),
        label: 'Type',
        type: 'select',
        items: BannerTypeValues,
      },

      tagId: {
        control: formControl<number | undefined>(undefined),
        type: 'select',
        label: 'Tag',
        invisible: true,
        items: this.tagApi.getAllTags().pipe(map((tags) => tags.map((v) => ({ value: v.id, label: v.name })))),
      },
      categoryId: {
        control: formControl<number | undefined>(undefined),
        type: 'select',
        label: 'Category',
        invisible: true,
        items: this.categoryApi.getAll().pipe(map((categories) => categories.map((v) => ({ value: v.id, label: v.name })))),
      },
      productId: {
        control: formControl<number | undefined>(undefined),
        type: 'select',
        label: 'Product',
        invisible: true,
        items: this.productApi.getAll().pipe(map((products) => products.map((v) => ({ value: v.id, label: v.name })))),
      },
      onHomePage: {
        control: formControl<boolean>(true, Validators.required),
        label: 'Home Page Banner',
        type: 'switch',
        items: EnableValues,
      },
      status: {
        control: formControl<boolean>(true, Validators.required),
        label: 'Enable',
        type: 'switch',
        items: EnableValues,
      },
    },
    onInit: (controls) => {
      controls.type.value$.pipe(takeUntilDestroyed()).subscribe((type) => {
        if (type == 0) {
          this.form.controls.categoryId.setValidators([Validators.required]);
        }
        if (type == 1) {
          this.form.controls.productId.setValidators([Validators.required]);
        }
        if (type == 2) {
          this.form.controls.tagId.setValidators([Validators.required]);
        }
        this.form.controls.tagId.updateValueAndValidity();
        this.form.controls.categoryId.updateValueAndValidity();
        this.form.controls.productId.updateValueAndValidity();
        this.form.setInvisible(type != 0, 'categoryId');
        this.form.setInvisible(type != 1, 'productId');
        this.form.setInvisible(type != 2, 'tagId');
      });
    },
  });

  get options() {
    return {
      fileType: 'Banner' as FileType,
    };
  }

  constructor() {
    super();
    const id = this.data?.banner?.id;
    if (id) {
      this.bannerApi
        .getBannerDetail(id)
        .pipe(takeUntilDestroyed())
        .subscribe((data) => {
          this.form.patchValue({
            title: data.banner.title,
            priority: data.banner.priority,
            type: data.banner.type,
            status: data.banner.status == 1 ? true : false,
          });
          if (data.banner.type == 0) {
            this.form.controls.categoryId.patchValue(data.banner.targetId);
          }
          if (data.banner.type == 1) {
            this.form.controls.productId.patchValue(data.banner.targetId);
          }
          if (data.banner.type == 2) {
            this.form.controls.tagId.patchValue(data.banner.targetId);
          }
          this.image.patchValue(data.banner.image.url);
        });
    }
  }

  onSubmit(e: ButtonClickEvent) {
    const values = this.form.getValue();
    if (!values) return;

    const id = this.data.banner?.id;
    const imagePath = this.getImagePath();
    const targetId = this.getTargetId();
    const createModel: BannerCreate = {
      ...values,
      imagePath,
      status: values.status ? 1 : 0,
      targetId: targetId!,
    };

    const rawUpdateModel = {
      id: id!,
      imagePath: imagePath,
      priority: values.priority,
      type: values.type,
      status: values.status ? 1 : 0,
      title: values.title,
      targetId: targetId,
      onHomePage: values.onHomePage,
    };

    const updateModel: BannerUpdate = APIHelper.nestValues(rawUpdateModel);

    (id ? this.bannerApi.updateBanner(updateModel) : this.bannerApi.create(createModel))
      .pipe(e.pipe())
      .subscribe(() => {
        this.submit(true);
      });
  }
  getImagePath(): string {
    const imageValue = this.image.value;
    const imagePath = (typeof imageValue === 'string' ? this.data.banner?.image?.path : imageValue?.path) ?? null;
    return imagePath!;
  }
  getTargetId(): number {
    const type = this.form.controls.type.value;
    if (type == 0) {
      return this.form.controls.categoryId.value!;
    }
    if (type == 1) {
      return this.form.controls.productId.value!;
    }
    if (type == 2) {
      return this.form.controls.tagId.value!;
    }
    return 0;
  }


}
